import { useState } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Camera,
  KeyRound,
  Loader2,
  PencilLine,
  Phone,
  Save,
  X,
  UserRound,
  LockKeyhole,
} from "lucide-react";


import "./supplier/MyPageSettings.css";

import { API_BASE_URL as API } from "../apiConfig";
export default function MyPage({ user, onUserUpdate }) {
  const { t } = useLanguage();
  const [avatarPreview, setAvatarPreview] = useState(user.avatar_url || user.avatar || "");
  const [profileForm, setProfileForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [activeTab, setActiveTab] = useState("profile");
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  const displayName = profileForm.name || user.name || user.email || "Guest";
  const customerCode = user.customer_code || "-";
  const memberSince = user.created_at ? new Date(user.created_at).getFullYear() : "-";
  const lastUpdated = formatDate(user.updated_at || user.modified_at || user.created_at);
  const orderCount = user.order_count ?? user.orders_count ?? user.total_orders ?? "-";
  const profileMeta = [
    {
      label: "Contact Person",
      value: user.contact_person || user.contact_name || user.representative_name || "-",
    },
    {
      label: "WhatsApp",
      value: user.whatsapp || user.whatsapp_phone || "-",
    },
    {
      label: "Country",
      value: user.country || user.country_region || "-",
    },
    {
      label: "Language",
      value: user.preferred_language || user.language || "-",
    },
  ];

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await axios.put(`${API}/auth/profile`, {
        name: profileForm.name,
        phone: profileForm.phone,
      });
      setMessage({ type: "success", text: t("profileUpdateSuccess") || "プロフィールを更新しました" });
      // Update parent state
      if (onUserUpdate) {
        onUserUpdate({ ...user, name: profileForm.name, phone: profileForm.phone });
      }
      setIsProfileEditing(false);
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || t("operationFailed") || "更新に失敗しました" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: "error", text: t("passwordMismatch") || "新しいパスワードが一致しません" });
      setLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setMessage({ type: "error", text: t("passwordTooShort") || "パスワードは6文字以上必要です" });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(`${API}/auth/password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      if (response.data?.access_token) {
        localStorage.setItem("token", response.data.access_token);
      }
      setMessage({ type: "success", text: t("passwordChangeSuccess") || "パスワードを変更しました" });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.detail || t("operationFailed") || "パスワード変更に失敗しました" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="supplier-account-page">
      <h2 className="account-title">{t("myPage") || "My Page"}</h2>
      <header className="account-identity">
        <label className="account-avatar" title="Upload avatar">
          {avatarPreview ? <img src={avatarPreview} alt={displayName} /> : <UserRound aria-hidden="true" />}
          <span className="account-avatar-camera"><Camera size={16} aria-hidden="true" /></span>
          <input type="file" accept="image/*" onChange={handleAvatarChange} className="sr-only" aria-label="Upload avatar" />
        </label>
        <div className="account-identity-name">
          <h3>{displayName}</h3>
          <p>{t("customerCode") || "Account code"} <strong>{customerCode}</strong></p>
        </div>
        <dl className="account-stats">
          <div><dt>Member since</dt><dd>{memberSince}</dd></div>
          <div><dt>Last updated</dt><dd>{lastUpdated}</dd></div>
          <div><dt>Total bookings</dt><dd>{orderCount}</dd></div>
        </dl>
      </header>

      <div className="account-tabs" role="tablist" aria-label="Account settings">
        {[["profile", "Profile", UserRound], ["security", "Security", LockKeyhole]].map(([id, label, Icon]) => (
          <button key={id} type="button" role="tab" id={`account-tab-${id}`}
            aria-selected={activeTab === id} aria-controls={`account-panel-${id}`}
            tabIndex={activeTab === id ? 0 : -1}
            onClick={() => { setActiveTab(id); setMessage({ type: "", text: "" }); }}
            onKeyDown={(event) => {
              if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
                event.preventDefault();
                const next = event.key === "Home" ? "profile" : event.key === "End" ? "security" : activeTab === "profile" ? "security" : "profile";
                setActiveTab(next);
                setMessage({ type: "", text: "" });
                document.getElementById(`account-tab-${next}`)?.focus();
              }
            }}>
            <Icon size={20} aria-hidden="true" />{label}
          </button>
        ))}
      </div>
      {message.text && <div role={message.type === "error" ? "alert" : "status"} className={`account-message account-message-${message.type}`}>{message.text}</div>}

      {activeTab === "profile" ? (
        <section role="tabpanel" id="account-panel-profile" aria-labelledby="account-tab-profile" className="account-profile">
          <div className="account-basic">
            <div className="account-section-heading">
              <h3>Basic details</h3>
              <button type="button" className="account-icon-button" disabled={loading}
                onClick={() => setIsProfileEditing(!isProfileEditing)}
                aria-label={isProfileEditing ? "Close profile edit" : "Edit profile"}
                title={isProfileEditing ? "Close profile edit" : "Edit profile"} data-testid="open-profile-edit-btn">
                {isProfileEditing ? <X size={18} /> : <PencilLine size={18} />}
              </button>
            </div>
            {isProfileEditing ? (
              <form onSubmit={handleProfileUpdate} className="account-form">
                <label>{t("companyName") || "Company name"} *
                  <input type="text" value={profileForm.name} required data-testid="profile-name"
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                </label>
                <label className="account-phone">{t("phone") || "Phone number"}
                  <span className="account-phone-input"><Phone size={17} aria-hidden="true" />
                    <input type="tel" value={profileForm.phone} data-testid="profile-phone"
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                  </span>
                </label>
                <div className="account-actions">
                  <button type="submit" className="account-primary" disabled={loading} data-testid="save-profile-btn">
                    {loading ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />}
                    {loading ? (t("saving") || "Saving...") : (t("saveChanges") || "Save changes")}
                  </button>
                  <button type="button" disabled={loading} onClick={() => {
                    setProfileForm({ name: user.name || "", phone: user.phone || "" });
                    setIsProfileEditing(false);
                  }}>{t("cancel") || "Cancel"}</button>
                </div>
              </form>
            ) : (
              <dl className="account-values">
                <InfoRow label={t("companyName") || "Company name"} value={profileForm.name} />
                <InfoRow label={t("phone") || "Phone number"} value={profileForm.phone} />
              </dl>
            )}
          </div>
          <div className="account-readonly">
            <section>
              <h3>{t("accountInfo") || "Account information"}</h3>
              <dl className="account-values">
                <InfoRow label={t("email") || "Email address"} value={user.email} locked />
                <InfoRow label={t("customerCode") || "Account code"} value={customerCode} locked />
              </dl>
            </section>
            <section>
              <h3>Contact information</h3>
              <dl className="account-values">{profileMeta.map((item) => <InfoRow key={item.label} {...item} />)}</dl>
            </section>
          </div>
        </section>
      ) : (
        <section role="tabpanel" id="account-panel-security" aria-labelledby="account-tab-security" className="account-security">
          <h3>{t("changePassword") || "Change password"}</h3>
          <form onSubmit={handlePasswordChange} className="account-form">
            <label>{t("currentPassword") || "Current password"} *
              <input type="password" autoComplete="current-password" required value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} data-testid="current-password" />
            </label>
            <label>{t("newPassword") || "New password"} *
              <input type="password" autoComplete="new-password" required value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} data-testid="new-password" />
            </label>
            <label>{t("confirmNewPassword") || "Confirm new password"} *
              <input type="password" autoComplete="new-password" required value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} data-testid="confirm-new-password" />
            </label>
            <p className="account-hint">{t("passwordMinLength") || "6 characters minimum"}</p>
            <button type="submit" className="account-primary" disabled={loading} data-testid="change-password-btn">
              {loading ? <Loader2 size={17} className="animate-spin" /> : <KeyRound size={17} />}
              {loading ? (t("saving") || "Saving...") : (t("changePassword") || "Change password")}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}

function InfoRow({ label, value, locked = false }) {
  return (
    <div className="account-value">
      <dt>{label}</dt>
      <dd>{value || "-"}{locked && <LockKeyhole size={15} aria-label="Read only" />}</dd>
    </div>
  );
}
